import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import SelectApplicationField from 'components/fields/SelectApplicationField';
import BoundaryFormFields from 'components/forms/BoundaryFormFields';

import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { APPLICATION_FIELD_NAME, TEST_ID } from './constants';
import type { CreateBoundaryFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateBoundaryForm: FunctionComponent<Props> = ({
    className = '',
    defaultValues,
    onCancelClose,
    onClose,
    onCreated,
    requestingClose,
    testId = TEST_ID
}) => {
    const { CREATE, NEXT_DRAW_THE_PERIMETER } = useTranslation();
    const navigate = useNavigate();
    const { createBoundaryPage } = usePageRoutes();

    const form = useForm<CreateBoundaryFormSchema>({
        defaultValues,
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = (data: CreateBoundaryFormSchema) => {
        const { application, field, name } = data;

        // Defer creation: carry the name + associations to the draw page, which creates
        // the boundary once geometry is drawn. Prevents persisting a geometry-less boundary.
        onCreated?.();

        navigate(createBoundaryPage, {
            state: {
                applicationId: application?.value,
                name,
                siteId: field?.value
            }
        });
    };

    return (
        <VerticalContainer className={`${styles.createBoundaryForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <BoundaryFormFields>
                        <SelectApplicationField<CreateBoundaryFormSchema>
                            control={control}
                            name={APPLICATION_FIELD_NAME}
                        />
                    </BoundaryFormFields>

                    <FormSubmitPanel
                        buttonText={CREATE}
                        errors={errors}
                        nextText={NEXT_DRAW_THE_PERIMETER}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateBoundaryForm;
